<script>
  /**
   * One review item, with the three actions staff can take.
   *
   * The Approve button is deliberately absent when there is no draft. Emergency
   * -tier items are created with `response_text` NULL on purpose, so a one-tap
   * Approve there would be approving nothing — the server refuses it too, but
   * offering a button that always errors is its own bug.
   */
  import { useRoute } from '@dvcol/svelte-simple-router';
  import api from '$lib/api.js';
  import { toast } from '$lib/toast.svelte.js';
  import { authStore } from '../stores/authStore.svelte.js';
  import PageHeader from '../components/PageHeader.svelte';

  const route = useRoute();
  const id = $derived(Number(route?.params?.id));

  let loading = $state(true);
  let error = $state(null);
  let item = $state(null);
  let notes = $state('');
  let overrideText = $state('');
  let showOverride = $state(false);
  let submitting = $state(false);

  const hasDraft = $derived(Boolean(item?.response_text));
  const isDone = $derived(Boolean(item?.human_reviewed || item?.override_response_text));

  async function load() {
    loading = true;
    error = null;
    try {
      item = await api.getReviewItem(id, { tenant_id: authStore.tenantId ?? undefined });
      overrideText = item.response_text ?? '';
    } catch (e) {
      error = e.message;
    } finally {
      loading = false;
    }
  }

  $effect(() => { if (id) load(); });

  function back() {
    window.history.pushState({}, '', '/strao/review');
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  /**
   * The server reports whether the message actually reached the guest. The
   * broker's fail-closed gate can decline a send even after a valid approval
   * (mock mode, or real sends not enabled), so never say "sent" unless it says so.
   */
  function reportOutcome(result, sentMessage, notSentMessage) {
    if (result.sent_to_guest) toast.success(sentMessage);
    else toast.info(notSentMessage);
  }

  async function act(kind) {
    submitting = true;
    try {
      if (kind === 'approve') {
        const result = await api.approveReviewItem(id, notes || null,
          { tenant_id: authStore.tenantId ?? undefined });
        reportOutcome(result, 'Approved and sent to the guest.',
          'Approved and recorded — but not delivered, because sending is disabled for this tenant.');
      } else if (kind === 'reject') {
        await api.rejectReviewItem(id, notes || null,
          { tenant_id: authStore.tenantId ?? undefined });
        toast.success('Rejected. Nothing was sent to the guest.');
      } else {
        const result = await api.overrideReviewItem(id, overrideText, notes || null,
          { tenant_id: authStore.tenantId ?? undefined });
        reportOutcome(result, 'Your reply was sent to the guest.',
          'Your reply was recorded — but not delivered, because sending is disabled for this tenant.');
      }
      back();
    } catch (e) {
      toast.error(e.message);
      await load();
    } finally {
      submitting = false;
    }
  }
</script>

<PageHeader title="Review item" subtitle={item?.property_name ?? 'Unlinked property'}>
  {#snippet children()}
    <button type="button" class="p-btn p-btn-quiet" onclick={back}>Back to queue</button>
  {/snippet}
</PageHeader>

{#if loading}
  <p class="p-card p-6 text-sm p-muted">Loading…</p>
{:else if error}
  <p class="p-card p-4 text-sm" style="color: var(--p-danger)">{error}</p>
{:else if item}
  {#if item.urgency_level === 'emergency'}
    <p class="mb-4 rounded-lg px-3 py-2 text-sm"
       style="background-color: var(--p-danger-bg); color: var(--p-danger)" role="alert">
      <i class="fas fa-triangle-exclamation" aria-hidden="true"></i>
      Emergency. No reply was drafted for this on purpose — read the message and
      write your own, or handle it outside the portal.
    </p>
  {/if}

  <section class="p-card mb-4 p-4">
    <h2 class="text-xs font-semibold uppercase tracking-wide p-faint">Guest message</h2>
    <p class="mt-1 text-xs p-faint">
      {item.guest_name ?? 'Unknown guest'}
      {#if item.hours_pending != null}
        · waiting {Math.round(item.hours_pending)}h
      {/if}
    </p>
    <p class="mt-3 whitespace-pre-wrap text-sm">{item.original_message_text ?? '—'}</p>
  </section>

  <section class="p-card mb-4 p-4">
    <h2 class="text-xs font-semibold uppercase tracking-wide p-faint">Drafted reply</h2>
    {#if hasDraft}
      <p class="mt-3 whitespace-pre-wrap text-sm">{item.response_text}</p>
      {#if item.canned_situation_name}
        <!-- This draft is the manager's own standing wording, not something
             inferred from property information. Saying so is what lets them
             skim-approve it with confidence — and, when it is wrong, tells them
             exactly which saved reply to go and fix. -->
        <p class="mt-3 text-xs p-muted">
          <i class="fas fa-comment-dots" aria-hidden="true"></i>
          Matched your saved reply <strong>{item.canned_situation_name}</strong> — this draft
          uses your own wording.
        </p>
      {/if}
      <p class="mt-3 text-xs p-faint">
        {item.response_type === 'escalated' ? 'Escalated for safety' : 'Held back for low confidence'}
        {#if item.confidence_score != null}
          · {Math.round(item.confidence_score * 100)}% confidence
        {/if}
        {#if item.trigger_reason}· {item.trigger_reason}{/if}
      </p>
    {:else}
      <p class="mt-3 text-sm p-muted">
        No draft was written for this item. Send your own reply below, or dismiss it.
      </p>
    {/if}
  </section>

  {#if isDone}
    <p class="p-card p-4 text-sm p-muted">
      Already handled{item.reviewed_by ? ` by ${item.reviewed_by}` : ''}.
      {#if item.override_response_text}
        A custom reply was sent instead of the draft.
      {:else if item.human_approved}
        The draft was approved.
      {:else}
        The draft was rejected.
      {/if}
    </p>
  {:else}
    <section class="p-card p-4">
      <label for="notes" class="text-xs font-medium p-muted">Notes (optional)</label>
      <input id="notes" class="p-input mt-1" bind:value={notes}
             placeholder="Why you made this call" />

      {#if showOverride}
        <label for="override" class="mt-4 block text-xs font-medium p-muted">
          Your reply — this is sent to the guest verbatim
        </label>
        <textarea id="override" class="p-input mt-1 h-32" bind:value={overrideText}></textarea>
      {/if}

      <div class="mt-5 flex flex-wrap justify-end gap-2">
        <button type="button" class="p-btn p-btn-quiet" disabled={submitting}
                onclick={() => act('reject')}>
          Reject
        </button>

        <button type="button" class="p-btn p-btn-quiet" disabled={submitting}
                onclick={() => (showOverride = !showOverride)}>
          {showOverride ? 'Cancel my reply' : 'Write my own reply'}
        </button>

        {#if showOverride}
          <button type="button" class="p-btn p-btn-primary"
                  disabled={submitting || !overrideText.trim()}
                  onclick={() => act('override')}>
            Send my reply
          </button>
        {:else if hasDraft}
          <button type="button" class="p-btn p-btn-primary" disabled={submitting}
                  onclick={() => act('approve')}>
            Approve &amp; send
          </button>
        {/if}
      </div>
    </section>
  {/if}
{/if}
